import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, resolveDynamicComponent as _resolveDynamicComponent, vShow as _vShow, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xs text-gray-500 mt-1" }
const _hoisted_2 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_3 = { class: "max-w-[1180px]" }
const _hoisted_4 = { class: "grid grid-cols-1 gap-5 sm:grid-cols-2" }
const _hoisted_5 = { class: "overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6" }
const _hoisted_6 = {
  key: 0,
  class: "mt-1 text-3xl font-semibold tracking-tight text-gray-900"
}
const _hoisted_7 = { class: "overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6" }
const _hoisted_8 = {
  key: 0,
  class: "mt-1 text-3xl font-semibold tracking-tight text-gray-900 h-9"
}
const _hoisted_9 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_10 = { class: "truncate" }
const _hoisted_11 = {
  key: 0,
  class: "max-w-[1180px]"
}
const _hoisted_12 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_13 = { class: "truncate" }
const _hoisted_14 = { class: "flex items-center gap-3 justify-end" }
const _hoisted_15 = {
  key: 0,
  class: "text-blue-600"
}
const _hoisted_16 = { class: "truncate" }
const _hoisted_17 = { class: "max-w-[1180px]" }
const _hoisted_18 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_19 = ["title"]
const _hoisted_20 = { class: "truncate" }
const _hoisted_21 = { class: "inline-flex gap-2 flex-wrap" }
const _hoisted_22 = {
  key: 0,
  class: "italic text-gray-500"
}
const _hoisted_23 = { key: 0 }

import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ImportWizardModal from '@/components/neue/ImportWizardModal.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import { useEntitlements } from '@/composables/useEntitlements'
import { useRouteTabs } from '@/composables/useRouteTabs'
import { useToast } from '@/composables/useToast'
import { listPath, LISTS_PATH, listSections, OVERVIEW_TAB, rootLists, SMART_LISTS_TAB } from '@/config/listSections'
import { canOpenTierView, hasMembershipProgram, tierViewPath } from '@/config/membershipTierView'
import { pairComparator } from '@/helpers/conversationSort'
import { localFilterToServer, newFilterOptions, serverFilterLabel } from '@/helpers/filters'
import { limitMessage, limitReached, toastPlanLimit } from '@/helpers/planLimits'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'

const ALL_CUSTOMERS_SLUG = 'bottle::all-customers'
const ABANDONED_CARTS_SLUG = 'bottle::abandoned-cart-customers'

export default {
  __name: 'ListsNeue',
  setup(__props) {

TimeAgo.addLocale(en)
const timeAgo = new TimeAgo('en-CA')

const relativeFormat = x => timeAgo.format(new Date(x))

const cachebuster = ref(0)
setInterval(() => cachebuster.value++, 60000)
const modalOpen = ref(false)
const importModalOpen = ref(false)

const router = useRouter()
const store = useStore()

const isCatering = computed(() => store.getters.isCatering)
const showMembershipTierLists = computed(() => !isCatering.value)
const tabs = computed(() => listSections(isCatering.value))
const { activeTab, sections } = useRouteTabs(tabs, LISTS_PATH)

onMounted(() => {
  api.call('GET', 'membership_tiers')
  api.call('GET', 'audiences')
  if (showMembershipTierLists.value) api.call('GET', 'membership_tiers/member_counts')
})

const tiers = computed(() => mapType('membership_tier')?.filter(x => x.attributes.is_active))
const tierList = tier => mapRelationship(tier?.relationships.audience.data)
// One server call counts every tier from the same contacts as the overview's
// all contacts, instead of a list size request per tier.
const memberCounts = computed(() => mapType('membership_tier_member_counts')[0]?.attributes)
const tierSize = tier => memberCounts.value && (memberCounts.value.counts[tier.id] ?? 0).toLocaleString()
const unassignedSize = computed(() => memberCounts.value?.unassigned ?? 0)
const { limit, user } = useEntitlements()
const opensTierView = computed(() => canOpenTierView(user.value))
const tierRowLink = tier => (opensTierView.value ? tierViewPath(tier.id) : '/lists/' + tierList(tier)?.id)
const tierRowDisabled = tier => !opensTierView.value && !tierList(tier)

const audiences = computed(() => rootLists(mapType('audience')))

const smartListsLimit = computed(() => limit('smart_lists'))
const smartListsLimitReached = computed(() =>
  limitReached(smartListsLimit.value, audiences.value.filter(a => !a.attributes.created_by_bottle).length)
)
const smartListsLimitMessage = computed(() => limitMessage('smart lists', smartListsLimit.value))
const audienceSize = audience =>
  mapRelationship(audience?.relationships.audience_detail.data)?.attributes.number_of_contacts.toLocaleString()

const OVERVIEW_SLUGS = [ALL_CUSTOMERS_SLUG, ABANDONED_CARTS_SLUG]

// Each list size is a request that counts the whole list, so only the lists
// the open tab shows are asked for, and only for the one number they show.
// The tier lists tab has its own counts.
const listsNeedingSizes = computed(() => {
  if (activeTab.value === SMART_LISTS_TAB.id) return audiences.value
  if (activeTab.value === OVERVIEW_TAB.id) {
    return audiences.value.filter(audience => OVERVIEW_SLUGS.includes(audience.attributes.slug))
  }
  return []
})

const requestedDetails = new Set()
// TODO(alicja): n+1 here!
watch(listsNeedingSizes, lists => {
  lists
    .filter(audience => !requestedDetails.has(audience.id))
    .forEach(audience => {
      requestedDetails.add(audience.id)
      api.call('GET', `audiences/${audience.id}/audience_details?fields=number_of_contacts`).catch(() => {})
    })
})

const allCustomers = computed(() => audiences.value?.find(x => x.attributes.slug === ALL_CUSTOMERS_SLUG))

const lastMessage = pair => {
  const detail = mapRelationship(pair.relationships.conversation_user_pair_detail.data)
  const recent = mapRelationships(detail?.relationships.most_recent_inbound_messages.data)
  return recent[0]
}

const pairIds = ref([])
const pairs = computed(() =>
  mapRelationships(pairIds.value.map(id => ({ type: 'conversation_user_pair', id }))).sort(
    pairComparator('recent', 'desc')
  )
)

const loadPairs = () =>
  api
    .call(
      'GET',
      'conversation_user_pairs?included=conversation_user_pair_detail,conversation_user_pair_detail.most_recent_inbound_messages'
    )
    .then(response => {
      pairIds.value = response.data.data.map(pair => pair.id)
    })

loadPairs()

const { showToast } = useToast()

const makeAudienceWithFilter = async filter => {
  const response = await api
    .call('POST', 'audiences', {
      audience: {
        is_root: false,
        audience_name: `Filtered list (created ${new Intl.DateTimeFormat([], {
          dateStyle: 'short',
          timeStyle: 'short'
        }).format(new Date())})`,
        root_audience_id: allCustomers.value.id,
        filter_groups_attributes: [{ filters_attributes: [localFilterToServer(filter)] }]
      }
    })
    .catch(toastPlanLimit(showToast))
  if (!response) return
  await router.push('/lists/' + response.data.data.id)
}

const abandonedCarts = computed(() => audiences.value?.find(x => x.attributes.slug === ABANDONED_CARTS_SLUG))

const canReadCustomers = computed(() => privilegeService.canAccessResourceAction('customers', null, 'read'))
const canUpdateMembershipTiers = computed(
  () => hasMembershipProgram(user.value) && privilegeService.canAccessResourceAction('sell', null, 'update')
)
const canReadMessages = computed(() => privilegeService.canAccessResourceAction('messages', null, 'read'))
const canReadCustomerProfile = computed(() =>
  privilegeService.canAccessResourceAction('customer_profile', null, 'read')
)
const canAccessCustomerLink = computed(() => canReadMessages.value || canReadCustomerProfile.value)
const canUpdateCustomers = computed(() => privilegeService.canAccessResourceAction('customers', null, 'update'))

const filterText = audience => {
  const filterGroups = mapRelationships(audience.relationships.filter_groups.data)
  // TODO(alicja): make sure this always holds
  console.assert(filterGroups.length <= 1)
  const filterGroup = filterGroups[0]
  const filters = mapRelationships(filterGroup?.relationships.filters.data)

  return filters.map(serverFilterLabel)
}

return (_ctx, _cache) => {
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_RouterLink = _resolveComponent("RouterLink")
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_NewContactModal = _resolveComponent("NewContactModal")
  const _component_BasicSpinner = _resolveComponent("BasicSpinner")
  const _component_router_link = _resolveComponent("router-link")
  const _component_DescriptionHeader = _resolveComponent("DescriptionHeader")
  const _component_NewFilterDropdown = _resolveComponent("NewFilterDropdown")
  const _component_TablistHeader = _resolveComponent("TablistHeader")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueWell = _resolveComponent("NeueWell")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, {
        text: "Customers",
        sections: _unref(sections)
      }, {
        right: _withCtx(() => [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[0] || (_cache[0] = $event => (modalOpen.value = true))
          }, {
            default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
              _createTextVNode("+ Add customer", -1)
            ]))]),
            _: 1
          }),
          (_unref(activeTab) === 'membership' && canUpdateMembershipTiers.value)
            ? (_openBlock(), _createBlock(_component_RouterLink, {
                key: 0,
                to: "/store/membership-tiers/new"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_NeueButton, { type: "primary" }, {
                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                      _createTextVNode("+ Tier", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_unref(activeTab) === 'smart' && canUpdateCustomers.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (smartListsLimitReached.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("p", _hoisted_1, _toDisplayString(smartListsLimitMessage.value), 1),
                      _createVNode(_component_NeueButton, {
                        type: "inactive",
                        title: smartListsLimitMessage.value
                      }, {
                        default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                          _createTextVNode("+ List", -1)
                        ]))]),
                        _: 1
                      }, 8, ["title"])
                    ], 64))
                  : (_openBlock(), _createBlock(_component_RouterLink, {
                      key: 1,
                      to: _unref(listPath)('new')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_NeueButton, { type: "primary" }, {
                          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                            _createTextVNode("+ List", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["to"]))
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["sections"])
    ]),
    main: _withCtx(() => [
      _createVNode(_component_NewContactModal, {
        "is-open": modalOpen.value,
        onCloseModal: _cache[1] || (_cache[1] = $event => (modalOpen.value = false))
      }, null, 8, ["is-open"]),
      _createVNode(ImportWizardModal, {
        "is-open": importModalOpen.value,
        "import-type": "UsersImport",
        onClose: _cache[2] || (_cache[2] = $event => (importModalOpen.value = false)),
        onCompleted: loadPairs
      }, null, 8, ["is-open"]),
      _createElementVNode("div", _hoisted_2, [
        _withDirectives(_createElementVNode("div", _hoisted_3, [
          _createElementVNode("dl", _hoisted_4, [
            _createVNode(_component_router_link, {
              to: '/lists/' + allCustomers.value?.id,
              class: _normalizeClass(!allCustomers.value && 'pointer-events-none')
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _cache[11] || (_cache[11] = _createElementVNode("dt", { class: "text-sm font-medium text-gray-500" }, "All contacts", -1)),
                  (audienceSize(allCustomers.value) != null)
                    ? (_openBlock(), _createElementBlock("dd", _hoisted_6, _toDisplayString(audienceSize(allCustomers.value)), 1))
                    : (_openBlock(), _createBlock(_component_BasicSpinner, {
                        key: 1,
                        size: "custom",
                        class: "mt-2 h-8"
                      }))
                ])
              ]),
              _: 1
            }, 8, ["to", "class"]),
            _createVNode(_component_router_link, {
              to: '/lists/' + abandonedCarts.value?.id,
              class: _normalizeClass(!abandonedCarts.value && 'pointer-events-none')
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _cache[12] || (_cache[12] = _createElementVNode("dt", { class: "text-sm font-medium text-gray-500" }, "Abandoned carts", -1)),
                  (audienceSize(abandonedCarts.value) != null)
                    ? (_openBlock(), _createElementBlock("dd", _hoisted_8, _toDisplayString(audienceSize(abandonedCarts.value)), 1))
                    : (_openBlock(), _createBlock(_component_BasicSpinner, {
                        key: 1,
                        size: "custom",
                        class: "mt-2 h-8"
                      }))
                ])
              ]),
              _: 1
            }, 8, ["to", "class"])
          ]),
          _createVNode(_component_DescriptionHeader, { name: "Customers" }),
          _createElementVNode("div", {
            class: _normalizeClass([[!allCustomers.value && 'pointer-events-none opacity-50'], "flex items-center justify-start sm:justify-end gap-2 mb-4 flex-wrap"])
          }, [
            _createVNode(_component_NewFilterDropdown, {
              direction: "left",
              name: "Cart & payment filters",
              filters: _unref(newFilterOptions).cart,
              onAddFilter: _cache[3] || (_cache[3] = type => makeAudienceWithFilter({ type }))
            }, null, 8, ["filters"]),
            _createVNode(_component_NewFilterDropdown, {
              direction: "left",
              name: "Fulfillment filters",
              filters: _unref(newFilterOptions).fulfillment,
              onAddFilter: _cache[4] || (_cache[4] = type => makeAudienceWithFilter({ type }))
            }, null, 8, ["filters"]),
            _createVNode(_component_NewFilterDropdown, {
              direction: "left",
              name: "Contact filters",
              filters: _unref(newFilterOptions).contact,
              onAddFilter: _cache[5] || (_cache[5] = type => makeAudienceWithFilter({ type }))
            }, null, 8, ["filters"])
          ], 2),
          (pairs.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                head: _withCtx(() => [
                  _createVNode(_component_TablistHeader, null, {
                    default: _withCtx(() => [
                      _createVNode(TablistGrid, {
                        columns: ['Customer', 'Last Message', 'Sent'],
                        header: ""
                      }, {
                        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                          _createElementVNode("div", null, "Customer", -1),
                          _createElementVNode("div", null, "Last Message", -1),
                          _createElementVNode("div", null, "Sent", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pairs.value, (pair) => {
                    return (_openBlock(), _createBlock(_component_TablistRow, {
                      key: pair.id
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(canAccessCustomerLink.value ? 'router-link' : 'div'), {
                          to: canAccessCustomerLink.value ? '/messages/' + pair.id : undefined,
                          class: _normalizeClass(['w-full', canAccessCustomerLink.value && 'cursor-pointer'])
                        }, {
                          default: _withCtx(() => [
                            _createVNode(TablistGrid, { columns: ['Customer', 'Last Message', 'Sent'] }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_9, _toDisplayString(pair.attributes.display_name), 1),
                                _createElementVNode("div", _hoisted_10, _toDisplayString(lastMessage(pair)?.attributes.body_decoded), 1),
                                (_openBlock(), _createElementBlock("div", {
                                  key: cachebuster.value,
                                  class: "truncate"
                                }, _toDisplayString(relativeFormat(pair.attributes.last_sent_timestamp)), 1))
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["to", "class"]))
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (!pairs.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueWell, { key: 1 }, {
                default: _withCtx(() => [
                  _createVNode(_component_EmptyState, {
                    plural: "customers",
                    description: ""
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, _unref(activeTab) === 'overview']
        ]),
        (showMembershipTierLists.value)
          ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_11, [
              (tiers.value?.length)
                ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                    head: _withCtx(() => [
                      _createVNode(_component_TablistHeader, null, {
                        default: _withCtx(() => [
                          _createVNode(TablistGrid, {
                            columns: ['Tier Name', 'Members', ''],
                            header: ""
                          }, {
                            default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                              _createElementVNode("div", null, "Tier Name", -1),
                              _createElementVNode("div", null, "Members", -1),
                              _createElementVNode("div", null, null, -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiers.value, (tier) => {
                        return (_openBlock(), _createBlock(_component_TablistRow, {
                          key: tier.id,
                          class: "tier-row"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_router_link, {
                              class: _normalizeClass([tierRowDisabled(tier) && 'pointer-events-none', "w-full cursor-pointer"]),
                              to: tierRowLink(tier)
                            }, {
                              default: _withCtx(() => [
                                _createVNode(TablistGrid, { columns: ['Tier Name', 'Members', ''] }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_12, _toDisplayString(tier.attributes.tier_name), 1),
                                    _createElementVNode("div", _hoisted_13, _toDisplayString(tierSize(tier)), 1),
                                    _createElementVNode("div", _hoisted_14, [
                                      (canReadCustomers.value)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_15, "View"))
                                        : _createCommentVNode("", true),
                                      (canUpdateMembershipTiers.value)
                                        ? (_openBlock(), _createBlock(_component_router_link, {
                                            key: 1,
                                            class: "text-blue-600",
                                            to: `/store/membership-tiers/${tier.id}/edit`,
                                            onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"]))
                                          }, {
                                            default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                              _createTextVNode(" Edit ", -1)
                                            ]))]),
                                            _: 1
                                          }, 8, ["to"]))
                                        : _createCommentVNode("", true)
                                    ])
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["class", "to"])
                          ]),
                          _: 2
                        }, 1024))
                      }), 128)),
                      (unassignedSize.value > 0)
                        ? (_openBlock(), _createBlock(_component_TablistRow, {
                            key: 0,
                            class: "tier-row text-gray-400",
                            title: "Contacts who have not been placed on a membership tier"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, { columns: ['Tier Name', 'Members', ''] }, {
                                default: _withCtx(() => [
                                  _cache[16] || (_cache[16] = _createElementVNode("div", { class: "font-medium italic truncate" }, "Unassigned", -1)),
                                  _createElementVNode("div", _hoisted_16, _toDisplayString(unassignedSize.value.toLocaleString()), 1),
                                  _cache[17] || (_cache[17] = _createElementVNode("div", null, null, -1))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (!tiers.value?.length)
                ? (_openBlock(), _createBlock(_component_NeueWell, { key: 1 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_EmptyState, { plural: "membership tiers" })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ], 512)), [
              [_vShow, _unref(activeTab) === 'membership']
            ])
          : _createCommentVNode("", true),
        _withDirectives(_createElementVNode("div", _hoisted_17, [
          (audiences.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                head: _withCtx(() => [
                  _createVNode(_component_TablistHeader, null, {
                    default: _withCtx(() => [
                      _createVNode(TablistGrid, {
                        columns: ['Name', 'Filters', 'Members'],
                        header: ""
                      }, {
                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                          _createElementVNode("div", null, "Name", -1),
                          _createElementVNode("div", null, "Filters", -1),
                          _createElementVNode("div", null, "Members", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(audiences.value, (audience) => {
                    return (_openBlock(), _createBlock(_component_TablistRow, {
                      key: audience.id
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_router_link, {
                          class: "w-full cursor-pointer",
                          to: '/lists/' + audience.id
                        }, {
                          default: _withCtx(() => [
                            _createVNode(TablistGrid, { columns: ['Name', 'Filters', 'Members'] }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_18, [
                                  _createTextVNode(_toDisplayString(audience.attributes.audience_name) + " ", 1),
                                  (audience.attributes.over_plan_limit)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: "ml-2 px-2 text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-700",
                                        title: smartListsLimitMessage.value
                                      }, " Over plan limit ", 8, _hoisted_19))
                                    : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("div", _hoisted_20, [
                                  _createElementVNode("span", _hoisted_21, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filterText(audience), (text) => {
                                      return (_openBlock(), _createElementBlock("span", {
                                        key: text,
                                        class: "inline-flex items-center px-2 py-0.5 text-blue-700 text-xs font-medium bg-blue-100 rounded-full"
                                      }, _toDisplayString(text), 1))
                                    }), 128)),
                                    (!filterText(audience).length)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_22, "No filters"))
                                      : _createCommentVNode("", true)
                                  ])
                                ]),
                                _createElementVNode("div", null, [
                                  (audienceSize(audience) != null)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(audienceSize(audience)), 1))
                                    : (_openBlock(), _createBlock(_component_BasicSpinner, {
                                        key: 1,
                                        class: "inline",
                                        size: "small"
                                      }))
                                ])
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["to"])
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (!audiences.value?.length)
            ? (_openBlock(), _createBlock(_component_NeueWell, { key: 1 }, {
                default: _withCtx(() => [
                  _createVNode(_component_EmptyState, { plural: "smart lists" })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, _unref(activeTab) === 'smart']
        ])
      ])
    ]),
    _: 1
  }))
}
}

}